import { DiceResult } from './DiceRoller';

// Extended result type that includes advantage/disadvantage data
interface ExtendedDiceResult extends DiceResult {
  advDisRolls?: [number, number];
  discardedValue?: number;
}

interface DiceResultDisplayProps {
  result: DiceResult | ExtendedDiceResult;
  rollType?: 'normal' | 'advantage' | 'disadvantage';
  rollLabel?: string;
  rollLabelColor?: string;
}

/**
 * Two-row dice result card with Swiss typography.
 *
 * Row 1: [LABEL] | notation | ADV badge
 * Row 2: math breakdown (left) → total (right)
 *
 * Colored left border when roll label is present.
 * Roller name is shown in the chat message header, not here.
 */
export function DiceResultDisplay({
  result,
  rollType = 'normal',
  rollLabel,
  rollLabelColor,
}: DiceResultDisplayProps) {
  const extResult = result as ExtendedDiceResult;

  const diceSum = result.rolls.reduce((sum, die) => sum + die.value, 0);
  // For adv/dis rolls, the modifier is total minus the chosen die (not sum of all dice)
  const modifier = extResult.advDisRolls
    ? result.total - (rollType === 'advantage'
        ? Math.max(...extResult.advDisRolls)
        : Math.min(...extResult.advDisRolls))
    : result.total - diceSum;

  // Detect crit/fumble — for adv/dis use the ACCEPTED die, not either raw roll
  const isD20 = result.rolls.length >= 1 && result.rolls[0].sides === 20;
  const rawD20 = isD20
    ? (extResult.advDisRolls
        ? (rollType === 'advantage' ? Math.max(...extResult.advDisRolls) : Math.min(...extResult.advDisRolls))
        : result.rolls[0].value)
    : null;
  const isCritResult = rawD20 === 20;
  const isFumbleResult = rawD20 === 1;

  const borderColor = rollLabelColor
    || (isCritResult ? 'var(--color-success)' : isFumbleResult ? 'var(--color-error)' : 'var(--color-accent)');

  // Crit/fumble get heavier, more dramatic frames
  const edgeBorderWidth = isCritResult || isFumbleResult ? 2 : 1;
  const edgeBorderColor = isCritResult ? '#d4af37' : isFumbleResult ? '#8b0000' : 'var(--color-border)';
  const edgeBorder = `${edgeBorderWidth}px solid ${edgeBorderColor}`;

  // Background tint for crits (golden wash) and fumbles (blood wash)
  const bgColor = isCritResult
    ? 'rgba(212, 175, 55, 0.06)'
    : isFumbleResult
      ? 'rgba(139, 0, 0, 0.08)'
      : 'var(--color-surface)';

  return (
    <div style={{
      backgroundColor: bgColor,
      border: edgeBorder,
      borderLeft: rollLabel ? 'none' : edgeBorder,
      borderRadius: 'var(--radius-sm)',
      padding: '10px 14px',
      paddingLeft: rollLabel ? 18 : 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: isCritResult
        ? '0 0 12px rgba(212, 175, 55, 0.15), inset 0 0 20px rgba(212, 175, 55, 0.05)'
        : isFumbleResult
          ? '0 0 12px rgba(139, 0, 0, 0.2), inset 0 0 20px rgba(139, 0, 0, 0.08)'
          : 'none',
    }}>
      {/* Ornamental left accent — tapered gradient bar with diamond */}
      {rollLabel && (
        <>
          {/* Tapered bar: fades at top and bottom like a scroll flourish */}
          <span style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: `linear-gradient(180deg, transparent 0%, ${borderColor} 20%, ${borderColor} 80%, transparent 100%)`,
            opacity: 0.7,
          }} />
          {/* Diamond ornament at vertical center */}
          <span style={{
            position: 'absolute',
            left: -1,
            top: '50%',
            width: 6,
            height: 6,
            transform: 'translateY(-50%) rotate(45deg)',
            backgroundColor: borderColor,
            boxShadow: `0 0 6px ${borderColor}`,
          }} />
        </>
      )}

      {/* Row 1: identity + context */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
      }}>
        {/* Roll label (left-most) */}
        {rollLabel && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: rollLabelColor || 'var(--color-accent)',
            flexShrink: 0,
          }}>
            {rollLabel}
          </span>
        )}

        {/* Notation */}
        <span style={{
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-muted)',
          flexShrink: 0,
        }}>
          {result.notation}
        </span>

        {/* Spacer */}
        <span style={{ flex: 1 }} />

        {/* ADV/DIS badge (right-aligned) */}
        {rollType !== 'normal' && (
          <span style={{
            padding: '1px 6px',
            borderRadius: 3,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
            backgroundColor: rollType === 'advantage'
              ? 'rgba(81, 207, 102, 0.15)'
              : 'rgba(255, 107, 107, 0.15)',
            color: rollType === 'advantage' ? 'var(--color-success)' : 'var(--color-error)',
            flexShrink: 0,
            letterSpacing: '0.04em',
          }}>
            {rollType === 'advantage' ? 'ADV' : 'DIS'}
          </span>
        )}
      </div>

      {/* Row 2: math (left) → total (right) */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
      }}>
        {/* Math breakdown */}
        <div style={{
          flex: 1,
          fontSize: 15,
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-muted)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 4,
        }}>
          {extResult.advDisRolls ? (
            // Advantage/Disadvantage: show both raw rolls
            <span>
              {extResult.advDisRolls.map((val, i) => {
                const isChosen = val !== extResult.discardedValue;
                return (
                  <span key={i}>
                    {i > 0 && <span style={{ margin: '0 3px', opacity: 0.4 }}>/</span>}
                    <span style={{
                      color: isChosen ? '#fff' : 'var(--color-text-muted)',
                      textDecoration: isChosen ? 'none' : 'line-through',
                      opacity: isChosen ? 1 : 0.4,
                    }}>
                      {val}
                    </span>
                  </span>
                );
              })}
              {modifier !== 0 && (
                <span style={{ color: modifier > 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                  {' '}{modifier >= 0 ? '+' : ''}{modifier}
                </span>
              )}
              <span style={{ opacity: 0.4 }}>{' '}=</span>
            </span>
          ) : modifier !== 0 ? (
            // Normal roll with modifier
            <span>
              <span>{diceSum}</span>
              <span style={{ color: modifier > 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                {' '}{modifier >= 0 ? '+' : ''}{modifier}
              </span>
              <span style={{ opacity: 0.4 }}>{' '}=</span>
            </span>
          ) : null}
        </div>

        {/* Total */}
        <span style={{
          fontSize: isCritResult || isFumbleResult ? 26 : 22,
          fontWeight: 700,
          fontFamily: 'var(--font-display)',
          color: isCritResult ? '#ffd700' : isFumbleResult ? '#ff3333' : '#fff',
          textShadow: isCritResult
            ? '0 0 8px rgba(212, 175, 55, 0.6), 0 0 20px rgba(212, 175, 55, 0.3)'
            : isFumbleResult
              ? '0 0 8px rgba(200, 0, 0, 0.6), 0 0 20px rgba(139, 0, 0, 0.4)'
              : 'none',
          lineHeight: 1,
        }}>
          {result.total}
        </span>
      </div>
    </div>
  );
}

/**
 * Compact inline dice result for chat
 */
export function DiceResultInline({ result }: { result: DiceResult }) {
  const getDieColor = (sides: number): string => {
    switch (sides) {
      case 4: return 'var(--color-dice-d4)';
      case 6: return 'var(--color-dice-d6)';
      case 8: return 'var(--color-dice-d8)';
      case 10: return 'var(--color-dice-d10)';
      case 12: return 'var(--color-dice-d12)';
      case 20: return 'var(--color-dice-d20)';
      default: return 'var(--color-accent)';
    }
  };

  const isCrit = (value: number, sides: number) => value === sides;
  const isFumble = (value: number) => value === 1;

  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 'var(--font-size-sm)',
    }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{result.notation} = </span>
      <span style={{ color: 'var(--color-text-secondary)' }}>[</span>
      {result.rolls.map((die, i) => {
        const crit = isCrit(die.value, die.sides);
        const fumble = isFumble(die.value);
        return (
          <span key={i}>
            {i > 0 && <span style={{ color: 'var(--color-text-muted)' }}>, </span>}
            <span style={{
              color: crit
                ? 'var(--color-success)'
                : fumble
                  ? 'var(--color-error)'
                  : getDieColor(die.sides),
              fontWeight: crit || fumble ? 'var(--font-weight-bold)' : 'normal',
              textShadow: crit
                ? '0 0 5px var(--color-success)'
                : fumble
                  ? '0 0 5px var(--color-error)'
                  : 'none',
            }}>
              {die.value}
            </span>
          </span>
        );
      })}
      <span style={{ color: 'var(--color-text-secondary)' }}>]</span>
      <span style={{ color: 'var(--color-text-muted)' }}> = </span>
      <span style={{
        color: 'var(--color-accent)',
        fontWeight: 'var(--font-weight-bold)',
      }}>
        {result.total}
      </span>
    </span>
  );
}
